import { useEffect, useRef, useState } from "react";

import ArrowLeftIcon from "@heroicons/react/24/solid/ArrowLeftIcon";
import { SliceZone } from "@prismicio/react";
import qs from "qs";
import {
  FieldErrors,
  FieldValues,
  SubmitErrorHandler,
  SubmitHandler,
  useFormContext,
} from "react-hook-form";

import { Loader } from "components/Loader/Loader";
import PrimaryButton from "components/button/primaryButton/PrimaryButton";
import TextButton from "components/button/textButton/TextButton";
import ErrorBoundary from "components/errorBoundary/ErrorBoundary";
import TabbedForm from "components/form/tabbedForm/TabbedForm";
import { CreationFlowStepContext } from "contexts/CreationFlowStepContext";
import { Bugsnag } from "lib/bugsnag";
import {
  FormDocument,
  PreviewDocument,
  ProductDocumentDataCreationFlowStepsItem,
} from "prismicio-types";
import { useStatus } from "providers/status.context";
import {
  useGetCustomisation,
  useGetProduct,
  useSetCustomisation,
} from "queries/legacy/product-queries";
import { components as fieldComponents } from "slices/fields";
import { components as previewComponents } from "slices/previews";
import { trackFormBackEvent } from "tracking/events/track-form-back-event";
import { trackFormSubmitEvent } from "tracking/events/track-form-submit-event";
import { trackModerationErrorEvent } from "tracking/events/track-moderation-error-event";
import { trackModerationFailureEvent } from "tracking/events/track-moderation-failure-event";
import { trackTabSwitchActiveEvent } from "tracking/events/track-tab-switch-active-event";
import { trackTabSwitchInactiveEvent } from "tracking/events/track-tab-switch-inactive-event";
import { trackValidationErrorEvent } from "tracking/events/track-validation-error-event";
import { trackValidationFailureEvent } from "tracking/events/track-validation-failure-event";
import { MODERATION_FIELD_STATUS } from "types/moderation-constants";
import { ModerationResponse } from "types/queries";
import { SlicezoneContext } from "types/slicezone-context";
import { ErrorItem, SSR_CONFIGURATION_ERROR } from "utils/legacy/error-utils";
import { CANVAS_FALLBACK_CONTENT } from "utils/legacy/fallback-content";
import { useInterpolate } from "utils/legacy/prismic-utils";
import {
  getPrismicKeyFromErrorType,
  useGetTabsFromFormDocument,
  usePartialTocValidation,
} from "utils/legacy/schema-utils";
import { removeKeysWithPrefixfromObject } from "utils/remove-keys-with-prefix-from-object";

import styles from "./CreationFlowStep.module.scss";
import { MODERATED_PREFIX } from "./constants";
import { isAutoAdvancePending, markAutoAdvanceDone } from "./shouldAutoAdvance";
import { useCreationFlowStep } from "./useCreationFlowStep";

type CreationFlowStepProps = {
  currentStep: number;
  data: ProductDocumentDataCreationFlowStepsItem;
  numberOfSteps: number;
  onBack: () => void;
  onSuccess: (arg: qs.ParsedQs) => void;
};

export default function CreationFlowStep({
  currentStep,
  data,
  numberOfSteps,
  onBack,
  onSuccess,
}: CreationFlowStepProps) {
  const methods = useFormContext();

  const { data: product } = useGetProduct();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  const validate = usePartialTocValidation();
  const interpolate = useInterpolate();

  const { setStatus, status } = useStatus();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const stepSucceededRef = useRef(false);

  const formDocument = (data as any).form as FormDocument;

  // Temporary flags to indicate whether the form has a counter slice
  // TODO: Review the implementations of the counter and bottom navigation to
  // make if more flexible from the CMS side and handle global forms settings.
  const isIntroStep = formDocument.data.slices.some(
    (slice) => slice.slice_type === "intro_block"
  );
  const isLoadingStep = formDocument.data.slices.some(
    (slice) => slice.slice_type === "spinner"
  );

  const isGenreStep = formDocument.data.slices.some(
    (slice) =>
      slice.slice_type === "radio_group_field" &&
      (slice.variation === "withSubtitle" ||
        slice.variation === "withBookPreviewCard")
  );

  // Sticky actions currently map to continue button and step back button
  const shouldShowStickyActions = !isLoadingStep && !isLoadingStep;
  // Part of sticky actions but a level down to only hide the back navigation button
  const shouldShowBottomNavigation = !isGenreStep && !isLoadingStep;
  // Step count currnetly the step count shown at top of steps in PDC flow
  const shouldShowStepCount = !isIntroStep && !isLoadingStep;

  // Only a plain step one: a spinner step advances itself, a genre step needs a genre prefill never
  // carries, and a tabbed one only moves tabs. Tabs are read from the form document itself.
  const canAutoAdvance =
    currentStep === 1 &&
    !isLoadingStep &&
    !isGenreStep &&
    !formDocument.data.tabs.some((tab) => tab.muse_schema_keys);

  const [isCovered, setIsCovered] = useState(
    () => canAutoAdvance && isAutoAdvancePending()
  );

  // Success navigates away, so the cover has to outlive the submit or step one flashes back
  // for the length of the route transition. Any other outcome leaves the shopper here.
  const handleStepSuccess = (data: qs.ParsedQs) => {
    stepSucceededRef.current = true;
    onSuccess(data);
  };

  const tabs = useGetTabsFromFormDocument(formDocument);

  /**
   * `tabs` is derived from the live product schema and can shrink (e.g. a
   * shared Avatars tab disappearing once an in-flight schema/customisation
   * update resolves). If that happens while `activeTabIndex` points at a
   * since-removed tab, clamp it back into range so `TabbedForm` never
   * receives an out-of-bounds index.
   */
  useEffect(() => {
    if (tabs && activeTabIndex > tabs.length - 1) {
      setActiveTabIndex(tabs.length - 1);
    }
  }, [tabs, activeTabIndex]);

  // Continue Shopping prefill auto-advances step one through the real form submit, so it
  // validates and moderates like a click. Marked done first so StrictMode can't submit twice.
  useEffect(() => {
    if (!canAutoAdvance || !isAutoAdvancePending()) {
      return;
    }

    markAutoAdvanceDone();

    // Safari before 16 has no requestSubmit; the shopper presses Continue themselves.
    if (typeof formRef.current?.requestSubmit !== "function") {
      setIsCovered(false);
      return;
    }

    formRef.current.requestSubmit();
  }, [canAutoAdvance]);

  const { mutate: setCustomisation } = useSetCustomisation();
  const { data: customisation } = useGetCustomisation();
  const { hasModeratedFields, moderateFields } = useCreationFlowStep({
    currentStep,
  });

  const preview =
    (formDocument.data.preview as unknown as PreviewDocument).data ?? null;

  // Set validation errors in RHF, using messages from the Canvas Prismic document
  function setErrors(errors: ErrorItem[]) {
    errors.forEach((error) => {
      trackValidationFailureEvent(
        product!,
        `${formDocument?.data?.tracking_id}`,
        error.context.label,
        error.type,
        currentStep,
        activeTabIndex
      );

      methods.setError(error.context.label, {
        type: error.type,
        message: product!.content.canvas[
          getPrismicKeyFromErrorType(error.type)
        ] as string,
      });
    });
  }

  function handleModerationErrors(moderationResponse: ModerationResponse) {
    let hasErrors = false;
    let firstErrorField: string | null = null;

    Object.entries(moderationResponse.values).forEach(
      ([fieldKey, fieldResponse]) => {
        // NOTE we add the .value suffix to the field name here as the field name should match the `${key}.value` format
        const formFieldName = `${fieldKey}.value`;

        if (
          fieldResponse.status === MODERATION_FIELD_STATUS.MODERATION_FAILED ||
          fieldResponse.status ===
            MODERATION_FIELD_STATUS.MODERATION_FAILED_MOCK_TEMP
        ) {
          hasErrors = true;
          // NOTE added this to ensure we focus on the first field with an error
          if (!firstErrorField) {
            firstErrorField = formFieldName;
          }

          trackModerationFailureEvent(
            product!,
            `${formDocument?.data?.tracking_id}`,
            fieldKey,
            currentStep,
            activeTabIndex
          );

          methods.setError(
            formFieldName,
            {
              type: "validation-failed",
              message:
                (product!.content.canvas
                  .moderation_field_failure_text as string) ||
                CANVAS_FALLBACK_CONTENT.moderation_field_failure_text ||
                fieldResponse.validation_failure_reason,
            },
            { shouldFocus: formFieldName === firstErrorField }
          );
        } else if (fieldResponse.status === MODERATION_FIELD_STATUS.ERROR) {
          hasErrors = true;
          if (!firstErrorField) {
            firstErrorField = formFieldName;
          }

          trackModerationErrorEvent(
            product!,
            `${formDocument?.data?.tracking_id}`,
            fieldKey,
            currentStep,
            activeTabIndex
          );

          methods.setError(
            formFieldName,
            {
              type: "error",
              message:
                (product!.content.canvas
                  .moderation_field_error_message as string) ||
                CANVAS_FALLBACK_CONTENT.moderation_field_error_message,
            },
            { shouldFocus: formFieldName === firstErrorField }
          );

          Bugsnag.notify(
            new Error("Moderation API returned error status for field"),
            (event) => {
              event.addMetadata("moderation", {
                fieldKey,
                customisationId: moderationResponse.customisationId,
                fieldValue: fieldResponse.value,
              });
            }
          );
        }
      }
    );

    return hasErrors;
  }

  const validateSubmission = async (data: FieldValues) => {
    // Run local validation first
    if (!(await methods.trigger(undefined, { shouldFocus: true }))) {
      // Every other exit from here settles the status; without this one the flow is left
      // in "loading" and the Continue button spins with nothing to stop it.
      setStatus("idle");
      return false;
    }

    try {
      const { combinedCustomisation, errors } = await validate(data);

      if (errors) {
        /**
         * Active errors fall here.
         * These errors are related to the currently active form fields.
         */
        setErrors(errors);
        setStatus("idle");
        return false;
      }

      return combinedCustomisation;
    } catch (error) {
      /**
       * Error isn't validation-related.
       *
       * We don't want to close the form because this could be
       * a client-side (e.g. network) or temporary API issue (e.g. timeout)
       * that a retry would fix.
       */
      trackValidationErrorEvent(
        product!,
        `${formDocument?.data?.tracking_id}`,
        "system",
        currentStep,
        activeTabIndex
      );

      setStatus("error");
      return false;
    }
  };

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    setStatus("loading");

    trackFormSubmitEvent(
      product,
      `${formDocument?.data?.tracking_id}`,
      currentStep,
      activeTabIndex || 0
    );

    const { customisation_id } = customisation || {};
    const requiresModeration = hasModeratedFields(data);
    // Remove hidden fields used for moderation
    const sanitisedData = removeKeysWithPrefixfromObject(
      data,
      MODERATED_PREFIX
    );

    if (requiresModeration && customisation_id) {
      // moderateFields rethrows, and so does handleSubmit after it: uncaught, an outage left
      // Continue spinning on "loading" with nothing to stop it.
      let moderateResponse;

      try {
        // Send moderated fields to moderation API
        moderateResponse = await moderateFields(
          data,
          customisation_id as string,
          product.museSlug
        );
      } catch (error) {
        setStatus("error");

        Bugsnag.notify(
          error instanceof Error
            ? error
            : new Error("Moderation request failed"),
          (event) => {
            event.severity = "error";
            event.unhandled = false;
            event.addMetadata("moderation", {
              customisationId: customisation_id,
              product: product.museSlug,
            });
          }
        );

        return;
      }

      if (moderateResponse) {
        const hasModerationErrors = handleModerationErrors(moderateResponse);

        if (hasModerationErrors) {
          setStatus("idle");
          // NOTE if we have moderation errors we can save validating the remaining fields for now as these errors may
          // overwrite the note to the user about the moderation errors.
          return;
        }

        const moderatedValues = Object.fromEntries(
          Object.entries(moderateResponse.values).map(([key, value]) => {
            // We strip status and the failure reason here as its annoying having it in the customisation.
            const { status, validation_failure_reason, ...rest } = value;
            return [key, rest];
          })
        );

        const moderatedData = {
          ...sanitisedData,
          ...moderatedValues,
        };
        const combinedCustomisation = await validateSubmission(moderatedData);

        if (combinedCustomisation) {
          handleStepSuccess(combinedCustomisation);
        } else {
          // we want to update the customisation with the moderated data
          setCustomisation({ data: moderatedData });
        }
      }

      // NOTE if we don't receive a moderation response then we fall through to the usual TOC validation anyway
      // THis is ok because a required moderated field without a moderation hash will fail validation anyway.
      return;
    }

    const combinedCustomisation = await validateSubmission(sanitisedData);

    if (combinedCustomisation) {
      if (tabs && activeTabIndex < tabs.length - 1) {
        setCustomisation({ data: combinedCustomisation });
        setActiveTabIndex((t) => t + 1);
        setStatus("idle");
      } else {
        handleStepSuccess(combinedCustomisation);
      }
    }
  };

  const onInvalid: SubmitErrorHandler<FieldValues> = (errors) => {
    const trackErrors = (
      errorObj: FieldErrors<FieldValues>,
      parentKey = ""
    ) => {
      Object.entries(errorObj).forEach(([key, error]) => {
        const fieldName = parentKey ? `${parentKey}.${key}` : key;

        if (error && typeof error === "object") {
          // Check if this is a field error (has type property) or nested errors
          if ("type" in error && typeof error.type === "string") {
            trackValidationFailureEvent(
              product!,
              `${formDocument?.data?.tracking_id}`,
              fieldName,
              error.type,
              currentStep,
              activeTabIndex
            );
          } else {
            // Recursively handle nested field errors
            trackErrors(error as FieldErrors<FieldValues>, fieldName);
          }
        }
      });
    };

    trackErrors(errors);
  };

  async function onSwitchAndTrackTabClick(index: number, isDisabled?: boolean) {
    // Only track inactive clicks immediately (these don't go to validation)
    if (isDisabled) {
      trackTabSwitchInactiveEvent(
        product!,
        `${formDocument?.data?.tracking_id}`,
        currentStep,
        activeTabIndex || 0
      );
      return;
    }

    // Active clicks are tracked in onTabSwitch after validation succeeds
    return await onTabSwitch(index);
  }

  async function onTabSwitch(index: number) {
    if (index === activeTabIndex) {
      return;
    }

    await methods.handleSubmit(async (data) => {
      const combinedCustomisation = await validateSubmission(data);

      if (combinedCustomisation) {
        // Track AFTER successful validation only
        trackTabSwitchActiveEvent(
          product!,
          `${formDocument?.data?.tracking_id}`,
          currentStep,
          activeTabIndex || 0
        );

        setCustomisation({ data: combinedCustomisation });
        setActiveTabIndex(index);
        setStatus("idle");
      }
    })();
  }

  async function handleOnBack() {
    trackFormBackEvent(
      product!,
      `${formDocument?.data?.tracking_id}`,
      currentStep,
      activeTabIndex || 0
    );
    if (currentStep === 1) {
      onBack();
      setStatus("loading");
    } else if (!tabs || activeTabIndex === 0) {
      await methods.handleSubmit(async (data) => {
        // Remove hidden fields used for moderation
        const sanitisedData = removeKeysWithPrefixfromObject(
          data,
          MODERATED_PREFIX
        );
        const combinedCustomisation = await validateSubmission(sanitisedData);

        if (combinedCustomisation) {
          setCustomisation({ data: combinedCustomisation });
          onBack();
          setStatus("idle");
        }
      })();
    } else {
      onTabSwitch(activeTabIndex - 1);
    }
  }

  return (
    <CreationFlowStepContext.Provider
      value={{ onStepSuccess: handleStepSuccess }}
    >
      {isCovered && (
        <div className={styles.autoAdvanceCover} role="status">
          <Loader
            title={interpolate(
              product.content.canvas.form_loading_label ??
                CANVAS_FALLBACK_CONTENT.form_loading_label
            )}
          />
        </div>
      )}

      <div className={styles.stepTop}>
        {shouldShowStepCount && (
          <h1 className={styles.stepCountLabel}>
            {interpolate(
              product.content.canvas.creation_flow_step_count_label ??
                CANVAS_FALLBACK_CONTENT.creation_flow_step_count_label,
              {
                currentStep,
                numberOfSteps,
              }
            )}
          </h1>
        )}

        {data.title && (
          <h2 className={styles.stepTitle}>{interpolate(data.title)}</h2>
        )}
        {data.subtitle && (
          <p className={styles.stepSubtitle}>{interpolate(data.subtitle)}</p>
        )}
      </div>

      {preview && (
        <ErrorBoundary product={product}>
          <SliceZone<SlicezoneContext>
            components={previewComponents}
            slices={preview.slices}
            context={{
              formTrackingId: `${formDocument.data.tracking_id}`,
              currentStep,
              activeTabIndex,
            }}
          />
        </ErrorBoundary>
      )}

      <div className={styles.content}>
        <form
          ref={formRef}
          aria-label={formDocument.data.title || ""}
          className={styles.form}
          onSubmit={(event) => {
            event.stopPropagation();
            methods
              .handleSubmit(
                onSubmit,
                onInvalid
              )(event)
              .finally(() => {
                if (!stepSucceededRef.current) {
                  setIsCovered(false);
                }
              });
          }}
        >
          <div className={styles.fields}>
            {tabs ? (
              <TabbedForm
                activeTabIndex={activeTabIndex}
                onTabClick={onSwitchAndTrackTabClick}
                tabs={tabs}
              />
            ) : (
              <ErrorBoundary product={product}>
                <SliceZone<SlicezoneContext>
                  components={fieldComponents}
                  slices={formDocument.data.slices}
                  context={{
                    formTrackingId: `${formDocument.data.tracking_id}`,
                    currentStep,
                    activeTabIndex,
                  }}
                />
              </ErrorBoundary>
            )}
          </div>

          {shouldShowStickyActions && (
            <div className={styles.stickyActions}>
              <PrimaryButton isLoading={status === "loading"} type="submit">
                {status === "loading"
                  ? interpolate(
                      product.content.canvas.form_loading_label ??
                        CANVAS_FALLBACK_CONTENT.form_loading_label
                    )
                  : interpolate(
                      product.content.canvas
                        .creation_flow_submit_button_label ??
                        CANVAS_FALLBACK_CONTENT.creation_flow_submit_button_label
                    )}
              </PrimaryButton>
              {shouldShowBottomNavigation && (
                <TextButton
                  className={styles.backButton}
                  color="gray"
                  onClick={handleOnBack}
                  type="button"
                >
                  <ArrowLeftIcon className={styles.backIcon} />
                  {currentStep === 1
                    ? interpolate(
                        product.content.canvas.back_product_page_link ??
                          CANVAS_FALLBACK_CONTENT.back_product_page_link
                      )
                    : interpolate(
                        product.content.canvas.back_previous_step_link ??
                          CANVAS_FALLBACK_CONTENT.back_previous_step_link
                      )}
                </TextButton>
              )}
            </div>
          )}
        </form>
      </div>
    </CreationFlowStepContext.Provider>
  );
}
